'use client';

import { useEffect, useRef } from 'react';
import { analytics } from 'lib/analytics/analytics';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import styles from './window-of-opportunity.module.css';

/**
 * Window of Opportunity — 2026 edition. Hidden frontend-hiring easter egg: the
 * page says a passphrase out loud, `window.opportunity` is a Proxy, and the
 * brief is sealed with WebCrypto behind that passphrase.
 * Rationale, re-sealing steps and the do-NOT-do list:
 * docs/feature-window-of-opportunity-2026.md
 */

/** Move this to reopen the season. Flips the copy on its own — no commit. */
const CONTEST_CLOSES_AT = new Date('2026-08-12T23:59:59+05:30');

const SUBMIT_TO = 'ayush.p@ultrahuman.com';

/** Re-seal with these exact parameters after any brief edit — doc §3. */
const KDF_ITERATIONS = 150_000;
const SALT_B64 = 'dWgtd2luZG93LW9mLW9wcG9ydHVuaXR5LTIwMjYtczE=';
const IV_B64 = 'dWgtd29vLTIwMjYt';
const BRIEF_SEALED =
  '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';

const TOKEN_STORAGE_KEY = 'uh:woo:2026:token';

/** Rendered by console.table — object keys become the columns. */
const TASKS = [
  {
    Task: 'T #1',
    Build: 'The Roadmap scroll sequence',
    'What we read': 'scroll-linked reveal, self-drawing lines, 60fps',
    Weight: '70%',
  },
  {
    Task: 'T #2',
    Build: 'The Performance Lab hero',
    'What we read': 'live telemetry readout, dismissible pill, 360px up',
    Weight: '30%',
  },
];

/** 2025's line, kept. */
const HEADLINE = "Sometimes they don't open a door, but a window.";

type LineKind = 'say' | 'in' | 'out' | 'warn';

/**
 * The invitation, written as a console transcript. The last two lines are the
 * passphrase, said out loud but not spelled out. `kind` only drives styling.
 */
const SCRIPT: Array<{ text: string; kind: LineKind }> = [
  { text: '> window.opportunity', kind: 'in' },
  { text: '< Proxy { open: async ƒ, help: ƒ }', kind: 'out' },
  { text: '> await window.opportunity.open()', kind: 'in' },
  { text: '! the window wants a passphrase.', kind: 'warn' },
  { text: 'your body is talking.', kind: 'say' },
  { text: 'we are listening.', kind: 'say' },
  { text: '^ three words. hyphenate them.', kind: 'warn' },
];

const LINE_KIND_CLASS: Record<LineKind, string> = {
  say: styles.say,
  in: '',
  out: styles.out,
  warn: styles.warn,
};

/** The line the caret sits after — the last thing said before you go and type. */
const CARET_AFTER = '^ three words. hyphenate them.';

/** Character pool for the scramble. Reads like a machine resolving a signal. */
const SCRAMBLE_CHARS = '01!<>-_\\/[]{}=+*^?#';

// Property reads a console or `await` performs on any object. Nudging on these
// would spam the seeker just for inspecting the Proxy.
const INTERNAL_PROBES = new Set([
  'then',
  'constructor',
  'prototype',
  'toJSON',
  'toString',
  'valueOf',
  'inspect',
  'nodeType',
  'length',
  'name',
]);

const bytesFromB64 = (b64: string) =>
  Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

/**
 * Accept what people actually type: spaces, stray punctuation and casing all
 * fold into the canonical hyphenated form. Getting the *words* right is the
 * puzzle, typing them exactly is not.
 */
const normalisePassphrase = (input: string) =>
  input
    .trim()
    .toLowerCase()
    .replace(/['"`]/g, '')
    .replace(/[\s_.]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');

/** PBKDF2 -> AES-GCM. Throws if the passphrase is wrong (GCM auth tag fails). */
async function unseal(passphrase: string): Promise<string> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: bytesFromB64(SALT_B64),
      iterations: KDF_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt'],
  );
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: bytesFromB64(IV_B64) },
    key,
    bytesFromB64(BRIEF_SEALED),
  );
  return new TextDecoder().decode(plaintext);
}

/** Stable per-browser id so a submission can be tied to a funnel. */
function seekerToken(): string {
  const mint = () => `WOO-2026-${crypto.randomUUID().slice(0, 8)}`;
  try {
    const existing = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (existing) return existing;
    const minted = mint();
    localStorage.setItem(TOKEN_STORAGE_KEY, minted);
    return minted;
  } catch {
    // Private mode / storage blocked — a per-session token still works.
    return mint();
  }
}

/**
 * Do NOT tidy these back into direct console calls: `compiler.removeConsole`
 * strips literal console *call expressions* in production, and the console
 * output IS this page. Reading the method through a variable survives — doc §5.
 */
type ConsoleFn = (...args: unknown[]) => void;

const consoleFn = (name: string): ConsoleFn => {
  const fn = (console as unknown as Record<string, unknown>)[name];
  return typeof fn === 'function' ? (fn as ConsoleFn).bind(console) : () => {};
};

const say = consoleFn('log');
const sayTable = consoleFn('table');
const groupOpen = consoleFn('group');
const groupClose = consoleFn('groupEnd');
const clearConsole = consoleFn('clear');

type OpportunityConsole = {
  open: (passphrase?: string) => Promise<string | undefined>;
  help: () => void;
};

declare global {
  interface Window {
    opportunity?: OpportunityConsole;
  }
}

const INK = {
  body: 'background:#000;color:#fff;padding:8px 12px;line-height:1.6',
  code: 'background:#000;color:#0ff;padding:8px 12px;font-family:monospace',
  dim: 'color:#888;padding:4px 12px',
  ring: 'padding:26px;border:7px solid #fff;border-radius:50%;font-size:0;line-height:0',
};

const WindowOfOpportunity = () => {
  useGlobalUI({ liteUI: true });
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const isClosed = Date.now() > CONTEST_CLOSES_AT.getTime();
    const closesOn = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(CONTEST_CLOSES_AT);

    analytics.track('woo:page_view', { year: 2026, closed: isClosed });
    clearConsole();

    const help = () => {
      say(
        `%cThe window opens with a passphrase.

It is on the page, said out loud. The last three words, hyphenated,
lower case. Then:

  await window.opportunity.open('...')`,
        INK.body,
      );
    };

    const open = async (passphrase?: string) => {
      analytics.track('woo:open_attempt', {
        year: 2026,
        withPassphrase: Boolean(passphrase),
      });

      if (!passphrase) {
        help();
        return undefined;
      }

      let brief: string;
      try {
        brief = await unseal(normalisePassphrase(passphrase));
      } catch {
        analytics.track('woo:open_failed', { year: 2026 });
        say(
          `%cThat is not it. The window stayed shut.

Read the page again — the answer is the part that is not code.`,
          INK.body,
        );
        return undefined;
      }

      const token = seekerToken();
      analytics.track('woo:brief_opened', { year: 2026, closed: isClosed });

      say('%c ', INK.ring);
      groupOpen('Window of Opportunity — 2026');
      say(
        `%c${
          isClosed
            ? `The window shut on ${closesOn}. The brief below still opens, and so
do we — send it anyway and we will read it.`
            : `The window is open until ${closesOn}.`
        }`,
        INK.body,
      );
      say(`%c${brief}`, INK.body);
      sayTable(TASKS);
      say(
        `%cYour seeker token: ${token}
Subject line: "Window of Opportunity — ${token}"  ->  ${SUBMIT_TO}`,
        INK.code,
      );
      groupClose();
      say('%cThe token is saved in localStorage, so it stays yours.', INK.dim);

      return token;
    };

    // A Proxy, so a wrong guess answers instead of returning undefined.
    window.opportunity = new Proxy({ open, help } as OpportunityConsole, {
      get(target, prop, receiver) {
        if (prop in target || typeof prop !== 'string') {
          return Reflect.get(target, prop, receiver);
        }
        if (!INTERNAL_PROBES.has(prop) && !prop.startsWith('_')) {
          say(
            `%cNo \`${prop}\` here. The window only knows two words: open, help.`,
            INK.dim,
          );
        }
        return undefined;
      },
    });

    return () => {
      delete window.opportunity;
    };
  }, []);

  /**
   * Decorative scramble-in — never a gate. Lines already render with their
   * final text, gsap is imported lazily (keeps ~100 KB out of first-load), and
   * it runs once. Doc §9 explains why all three matter.
   */
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined;
    }

    const targets = Array.from(
      stageRef.current?.querySelectorAll<HTMLElement>('[data-scramble]') ?? [],
    );
    if (!targets.length) return undefined;

    let cancelled = false;
    let killTweens: (() => void) | undefined;

    (async () => {
      try {
        const [{ gsap }, { ScrambleTextPlugin }] = await Promise.all([
          import('gsap'),
          import('gsap/ScrambleTextPlugin'),
        ]);
        if (cancelled) return;

        gsap.registerPlugin(ScrambleTextPlugin);
        killTweens = () => gsap.killTweensOf(targets);

        targets.forEach((el, i) => {
          const finalText = el.textContent ?? '';
          gsap.fromTo(
            el,
            { opacity: 0.35 },
            {
              opacity: 1,
              duration: 0.9,
              delay: 0.15 + i * 0.12,
              ease: 'none',
              scrambleText: {
                text: finalText,
                chars: SCRAMBLE_CHARS,
                speed: 0.6,
                revealDelay: 0.25,
              },
            },
          );
        });
      } catch {
        // Chunk failed, offline, plugin missing — the transcript is already
        // on screen and fully readable. Nothing to recover.
      }
    })();

    return () => {
      cancelled = true;
      killTweens?.();
    };
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.stage} ref={stageRef}>
        <h1 className={styles.headline}>{HEADLINE}</h1>
        {SCRIPT.map((line) => (
          <p
            key={line.text}
            className={`${styles.line} ${LINE_KIND_CLASS[line.kind]}`.trim()}
          >
            {/* Scramble target is this inner span, not the <p>: gsap rewrites
                its target's text, which would eat the caret. The 'say' lines
                opt out — they carry the passphrase. */}
            <span data-scramble={line.kind === 'say' ? undefined : ''}>
              {line.text}
            </span>
            {line.text === CARET_AFTER && (
              <span className={styles.caret} aria-hidden="true">
                ▋
              </span>
            )}
          </p>
        ))}
      </div>
    </div>
  );
};

export default WindowOfOpportunity;
